import styled, {  css } from 'styled-components';
import type {Theme as ThemeType} from '@/styles/Themes'

const selectVariants = (theme: ThemeType, variant = 'primary') =>
	({
		primary: css`
			border: 1.5px solid ${theme.background.primary};
			color: ${theme.background.primary};
		`,
		secondary: css`
			border: 1.5px solid ${theme.background.secondary};
			color: ${theme.background.secondary};
		`,
		base: css`
			border: 1.5px solid ${theme.background.disabled};
		`,
	}[variant]);

export const SelectContainer = styled.div<{
	variant?: TVariant;
}>`
	${({ theme, variant }) => selectVariants(theme, variant)}

	position: relative;
	min-width: 15em;
	min-height: calc(2.5em - 0.3rem);
	display: flex;
	align-items: center;
	gap: 0.5em;
	padding: 0.25rem 0.5rem;
	outline: none;
	cursor: pointer;

	&.disabled {
		color: ${({ theme }) => theme.background.disabled};
		border-color: ${({ theme }) => theme.background.disabled};
		cursor: not-allowed;
	}

	&.required {
		border-color: red;
		& > * {
			color: red;
		}
	}
`;

export const SelectValues = styled.span`
	flex-grow: 1;
	display: flex;
	gap: 0.5em;
	flex-wrap: wrap;
`;

export const SelectValueBadge = styled.button`
	display: flex;
	align-items: center;
	border: ${({ theme }) => `0.05em solid ${theme.background.primary}`};
	border-radius: 0.25em;
	padding: 0.15em 0.25em;
	gap: 0.25em;
	cursor: pointer;
	background: none;
	outline: none;
	transition: 0.2s ease-in-out;

	&:not(.disabled):hover,
	&:not(.disabled):focus {
		background-color: hsl(0, 100%, 90%);
		border-color: hsl(0, 100%, 50%);
	}

	&.disabled {
		background-color: #eee;
		color: ${({ theme }) => theme.background.disabled} !important;
		border-color: ${({ theme }) => theme.background.disabled};
		cursor: not-allowed;
	}
`;

const selectVariantsClearAll = (theme: ThemeType, variant = 'primary') =>
	({
		primary: css`
			color: ${theme.background.primary};
		`,
		secondary: css`
			color: white;
		`,
		none: css`
			color: #777;
		`,
		base: css`
			color: #777;
		`,
	}[variant]);

export const SelectClearAll = styled.button<{ variant?: TVariant }>`
	background: none;
	border: none;
	outline: none;
	cursor: pointer;
	padding: 0;
	font-size: 1.25em;
	transition: 0.25s ease-in-out;
	${({ theme, variant }) => selectVariantsClearAll(theme, variant)}
`;

const selectVariantsDivider = (theme: ThemeType, variant = 'primary') =>
	({
		primary: css`
			background-color: ${theme.background.primary};
		`,
		secondary: css`
			background-color: white;
		`,
		none: css`
			background-color: #777;
		`,
		base: css`
			background-color: #777;
		`,
	}[variant]);

export const SelectDivider = styled.div<{ variant?: TVariant }>`
	align-self: stretch;
	width: 0.05em;

	&.required {
		background-color: red;
	}

	&.disabled {
		background-color: ${({ theme }) => theme.background.disabled};
	}

	${({ theme, variant }) => selectVariantsDivider(theme, variant)}
`;

const selectVariantsCaret = (theme: ThemeType, variant = 'primary') =>
	({
		primary: css`
			border-top-color: ${theme.background.primary};
		`,
		secondary: css`
			border-top-color: white;
		`,
		none: css`
			border-top-color: #777;
		`,
		base: css`
			border-top-color: #777;
		`,
	}[variant]);

export const SelectCaret = styled.div<{ variant?: TVariant }>`
	translate: 0 25%;
	border: 0.25em solid transparent;

	&.required {
		border-top-color: red;
	}

	&.disabled {
		border-top-color: ${({ theme }) => theme.background.disabled};
	}

	${({ theme, variant }) => selectVariantsCaret(theme, variant)}
`;

export const SelectOptions = styled.ul<{ open: boolean; variant?: TVariant }>`
	margin: 0;
	padding: 0;
	list-style: none;
	position: absolute;
	top: 100%;
	left: -1px;
	right: -1px;
	max-height: 15em;
	overflow-y: auto;
	border: 0.05em solid;
	border-color: inherit !important;
	background-color: white;
	z-index: 100;
	display: ${({ open }) => (open ? 'block' : 'none')};
	box-shadow: 0 0.25em 0.5em rgba(0, 0, 0, 0.25);
`;

export const SelectOptionsAll = styled.li`
	color: black !important;
	display: flex;
	justify-content: center;
	gap: 1em;
	align-items: center;
	border-bottom: 0.05em solid #777;
	padding: 0.25em 0.5em;
	margin-bottom: 0.25em;

	position: sticky;
	top: 0;
	z-index: 999;
	background-color: white;

	& > * {
		color: black !important;
	}
`;

export const SelectOption = styled.li`
	text-align: left;
	padding: 0.25em 0.5em;
	cursor: pointer;

	color: black !important;

	&.selected {
		background-color: ${({ theme }) => theme.background.primary};
		color: white !important;
	}

	&.highlighted {
		background-color: ${({ theme }) => theme.background.primaryAlpha};
		color: white !important;
	}
`;

export const RangeContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;

	& > .thumb {
		position: absolute;
		pointer-events: none;
		height: 0;
		width: 200px;
		outline: none;

		&,
		&::-webkit-slider-thumb {
			-webkit-appearance: none;
			background-color: transparent;
			-webkit-tap-highlight-color: transparent;
		}

		&--left {
			z-index: 3;
		}

		&--right {
			z-index: 10;
		}

		&::-webkit-slider-thumb {
			background-color: #f1f5f7;
			border: none;
			border-radius: 50%;
			box-shadow: 0 0 1px 1px #ced4da;
			cursor: pointer;
			height: 18px;
			width: 18px;
			margin-top: 4px;
			pointer-events: all;
			position: relative;
		}

		&::-moz-range-thumb {
			background-color: #f1f5f7;
			border: none;
			border-radius: 50%;
			box-shadow: 0 0 1px 1px #ced4da;
			cursor: pointer;
			height: 18px;
			width: 18px;
			margin-top: 4px;
			pointer-events: all;
			position: relative;
		}
	}
`;

export const SliderContainer = styled.div`
	position: relative;
	width: 180px;
	.slider {
		&__track,
		&__range,
		&__left-value,
		&__right-value {
			position: absolute;
		}

		&__track,
		&__range {
			border-radius: 3px;
			height: 5px;
		}
		&__track {
			background-color: #ced4da;
			width: 100%;
			z-index: 1;
		}

		&__range {
			background-color: ${({ theme }) => theme.background.primary};
			z-index: 2;
		}

		&__left-value,
		&__right-value {
			color: inherit;
			font-size: 12px;
			margin-top: 15px;
		}
		&__left-value {
			left: 2px;
		}

		&__right-value {
			right: -4px;
		}
	}
`;
